import { BlurProcessor, loadProcessorError } from "../../utils/BlurProcessor";
import { LocalTrack } from "livekit-client";
import { cameraTrack, desiredMediaState } from "./state";
import { isCurrentSession, sessionActive } from "./pipe";
import { log } from "../../utils/logger";
import { segmenterReady } from "../../utils/segmenter";
import { BACKGROUND_PRESETS } from "../../utils/backgrounds";
import { createBackgroundSource } from "../../utils/gifBackground";
import type { BackgroundSource } from "../../utils/gifBackground";
import type { BackgroundEffect, MediaDesired } from "./state";

const key = "backgroundEffectSetting";

export function loadBlur() {
  desiredMediaState.blurSize = 15 / 1080
  const saved = localStorage.getItem(key)
  if(!saved) return
  try {
    const effect = JSON.parse(saved) as BackgroundEffect
    if(effect.kind === 'blur' && effect.strength !== 'weak' && effect.strength !== 'strong') return
    if(effect.kind === 'preset' && !BACKGROUND_PRESETS.some(p => p.id === effect.id)) return
    desiredMediaState.backgroundEffect = effect
  } catch {
    // ignore
  }
}

export function saveBlur() {
  localStorage.setItem(key, JSON.stringify(desiredMediaState.backgroundEffect))
}

function loadImage(src: string): Promise<HTMLImageElement> {
  return new Promise((resolve, reject) => {
    const img = new Image()
    img.crossOrigin = 'anonymous'
    img.onload = () => resolve(img)
    img.onerror = () => reject(new Error(`failed to load background image: ${src}`))
    img.src = src
  })
}

async function loadBackgroundImage(src: string): Promise<HTMLImageElement | null> {
  try {
    return await loadImage(src)
  } catch(e) {
    log.error('err load background image', e)
    return null
  }
}

// Кэш источников фона по URL, чтобы не пересоздавать и не перекодировать GIF
// при каждом needSync.
const backgroundSourceCache = new Map<string, Promise<BackgroundSource | null>>()

function getBackgroundSource(src: string): Promise<BackgroundSource | null> {
  const cached = backgroundSourceCache.get(src)
  if(cached) return cached
  const promise = loadBackgroundImage(src).then(async (image) => {
    if(!image) return null
    return createBackgroundSource(image)
  })
  backgroundSourceCache.set(src, promise)
  promise.then(s => { if(!s) backgroundSourceCache.delete(src) }).catch(() => backgroundSourceCache.delete(src))
  return promise
}

export function evictBackgroundSource(src: string | null) {
  if(!src) return
  const promise = backgroundSourceCache.get(src)
  if(promise) {
    promise.then(s => s?.destroy()).catch(() => {})
    backgroundSourceCache.delete(src)
  }
}

type BackgroundProcessorConfig =
  | { mode: 'blur'; blur: number; source: undefined }
  | { mode: 'background'; blur: number; source: BackgroundSource }

export async function getBackgroundProcessorConfig(
  desired: MediaDesired,
): Promise<BackgroundProcessorConfig | null> {
  const effect = desired.backgroundEffect
  if(effect.kind === 'blur') {
    return { mode: 'blur', blur: desired.blurSize, source: undefined }
  }
  if(effect.kind === 'preset') {
    const preset = BACKGROUND_PRESETS.find(p => p.id === effect.id)
    if(!preset) return null
    const source = await getBackgroundSource(preset.src)
    if(!source) return null
    return { mode: 'background', blur: 0, source }
  }
  if(effect.kind === 'custom') {
    if(!desired.customBackground) return null
    const source = await getBackgroundSource(desired.customBackground)
    if(!source) return null
    return { mode: 'background', blur: 0, source }
  }
  return null
}

/**
 * Навесить процессор фона на только что созданный трек камеры согласно
 * желаемому состоянию текущего прохода.
 */
export async function applyBlurProcessor(track: LocalTrack, desired: MediaDesired) {
  const config = await getBackgroundProcessorConfig(desired)
  if(config && !loadProcessorError.value && segmenterReady.value && isCurrentSession(desired.generation)) {
    log.debug('applyBlurProcessor')
    const processor = new BlurProcessor(config.blur, config.mode, config.source);
    await track.setProcessor(processor);
  }
}

/**
 * Свести процессор текущего трека камеры с желаемым состоянием прохода.
 * Готовность сегментера приходит событием (needSync из segmenter.ts), поэтому
 * бесконечный retry-цикл здесь не нужен.
 */
export async function blurTask(desired: MediaDesired) {
  if (!sessionActive.value) return
  if (!segmenterReady.value) return
  const track = cameraTrack.value
  if (!track) return
  const currentProcessor = track.getProcessor() as BlurProcessor | undefined
  const config = await getBackgroundProcessorConfig(desired)
  // Сессия или трек могли смениться, пока грузился конфиг (картинка фона).
  if (!isCurrentSession(desired.generation) || cameraTrack.value !== track) return
  const shouldProcess = !!config && !loadProcessorError.value
  if(shouldProcess && !currentProcessor) {
    log.debug('set processor in blurTask')
    const processor = new BlurProcessor(config!.blur, config!.mode, config!.source);
    await track.setProcessor(processor)
  } else if(!shouldProcess && currentProcessor) {
    log.debug('stop processor in blurTask')
    await track.stopProcessor()
  } else if(shouldProcess && currentProcessor && config) {
    if(currentProcessor.mode !== config.mode) {
      log.debug('set processor mode in blurTask')
      currentProcessor.setMode(config.mode)
    }
    if(config.mode === 'blur' && currentProcessor.blur !== config.blur) {
      log.debug('set new blur size in blurTask')
      currentProcessor.setNewBlurSize(config.blur)
    }
    if(config.mode === 'background' && currentProcessor.backgroundSource !== config.source) {
      log.debug('set new background source in blurTask')
      currentProcessor.setBackgroundSource(config.source)
    }
  }
}
